import React from 'react';
import { Container, Row, Col, Accordion } from 'react-bootstrap';
import { motion } from 'framer-motion';
import { Link, useNavigate } from 'react-router-dom';
import { FaArrowRight } from 'react-icons/fa';
import './Services.css';

// Assets
import heroBg from '../../Assets/Services/HeroService.png';
import service1 from '../../Assets/Services/Background.webp';
import service2 from '../../Assets/Services/Background-1.webp';
import service3 from '../../Assets/Services/Background-2.webp';
import service4 from '../../Assets/Services/Background-3.webp';
import faqImg from '../../Assets/Services/Insurance Agent.png';

const Services = () => {
    const navigate = useNavigate();

    const fadeUp = {
        hidden: { opacity: 0, y: 30 },
        visible: { opacity: 1, y: 0, transition: { duration: 0.6 } }
    };

    const serviceList = [
        {
            title: "Automobile Logistics",
            img: service1,
            link: "/services/automobile-logistics",
            desc: "Specialized car carrier solutions designed for leading OEMs, ensuring safe, damage-free, and timely vehicle transportation across India."
        },
        {
            title: "Chassis & Commercial Vehicle Transport",
            img: service2,
            link: "/services/chassis-transport",
            desc: "Secure and compliant transport of truck chassis, buses, tractors, and commercial vehicles using specially configured carriers."
        },
        {
            title: "ODC & Heavy Cargo Movement",
            img: service3,
            link: "/services/odc-heavy-cargo",
            desc: "Engineered solutions for Over-Dimensional Cargo including earth movers, heavy machinery, and industrial equipment."
        },
        {
            title: "Mining & Surface Transport",
            img: service4,
            link: "/services/mining-transport",
            desc: "Tailor-made mining logistics services for bulk movement and surface transport operations in challenging terrains."
        }
    ];

    const faqs = [
        { question: "How do you ensure damage-free vehicle transport?", answer: "Our carriers are specially designed for OEM standards, supported by trained drivers and strict loading protocols." },
        { question: "Do you handle Over-Dimensional Cargo permits?", answer: "Yes. We manage route surveys, permits, and escort coordination." },
        { question: "Is your fleet CMVR compliant?", answer: "Yes. All vehicles operate under CMVR compliance standards." },
        { question: "Can you handle long-term project logistics?", answer: "Yes. We provide dedicated fleet allocation and structured planning." }
    ];

    return (
        <div className="services-page-wrapper">

            {/* HERO */}
            <section className="services-hero">
                <img src={heroBg} alt="Logistics" className="hero-bg-img" />

                <Container className="hero-content text-center text-white">
                    <motion.div
                        initial={{ opacity: 0, y: 30 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.8 }}
                    >
                        <h1 className="display-4 fw-bold mb-3">
                            Integrated Logistics & Transport Solutions
                        </h1>
                        <p className="lead mx-auto hero-subtext">
                            Delivering specialized automobile, chassis, ODC, and mining logistics services
                            across India with precision, compliance, and operational excellence.
                        </p>
                    </motion.div>
                </Container>
            </section>

            {/* WHAT WE DO - OVERLAP SECTION */}
            <section className="what-we-do-section">
                <Container>
                    <div className="what-we-do-card">
                        <div className="text-center mb-5">
                            <h2 className="section-title fw-bold">What We Do?</h2>
                            <p className="text-muted">Discover a comprehensive range of tailored logistics services</p>
                        </div>

                        <Row>
                            {serviceList.map((srv, idx) => (
                                <Col md={6} key={idx} className="mb-4">
                                    <motion.div
                                        className="service-card-vertical clickable-card"
                                        initial="hidden"
                                        whileInView="visible"
                                        viewport={{ once: true }}
                                        variants={fadeUp}
                                        onClick={() => navigate(srv.link)}
                                    >
                                        <div className="img-container">
                                            <img src={srv.img} alt={srv.title} className="service-main-img" />
                                        </div>

                                        <div className="service-content">
                                            <h4 className="fw-bold mb-3">{srv.title}</h4>
                                            <p className="text-muted mb-4">{srv.desc}</p>
                                            <Link to={srv.link} className="red-link-text small fw-bold text-uppercase">
                                                Learn More <FaArrowRight className="ms-1" />
                                            </Link>
                                        </div>

                                        <div className="card-red-border"></div>
                                    </motion.div>
                                </Col>
                            ))}
                        </Row>
                    </div>
                </Container>
            </section>

            {/* STATS */}
            <section className="stats-section py-5 bg-white">
                <Container>
                    <Row className="align-items-center mb-5">
                        <Col lg={6}>
                            <h2 className="fw-bold display-5 mb-4">
                                Driven by Precision.<br /> Powered by Experience.
                            </h2>
                            <p className="text-muted">
                                With over four decades of industry presence, Janta Roadways delivers reliable,
                                compliant, and scalable logistics solutions tailored to automotive and industrial sectors.
                            </p>
                        </Col>

                        <Col lg={6}>
                            <Row>
                                {[
                                    { val: "40+", label: "Years Experience" },
                                    { val: "100%", label: "CMVR Compliant Fleet" },
                                    { val: "Pan-India", label: "Network Operational Coverage" },
                                    { val: "Dedicated", label: "Operations Control" }
                                ].map((stat, idx) => (
                                    <Col xs={6} key={idx} className="mb-4">
                                        <div className="stat-box p-4 text-center h-100">
                                            <h2 className="text-red fw-bold display-6 mb-2">{stat.val}</h2>
                                            <p className="small text-muted mb-0 text-uppercase fw-semibold">{stat.label}</p>
                                        </div>
                                    </Col>
                                ))}
                            </Row>
                        </Col>
                    </Row>
                </Container>
            </section>

            {/* FAQ */}
            <section className="faq-section py-5">
                <Container>
                    <div className="row mb-5">
                        <div className="col-12">
                            <span className="text-red fw-bold text-uppercase small d-block mb-2">FAQ</span>
                            <h2 className="fw-bold display-6">The Most Common Questions</h2>
                        </div>
                    </div>

                    <Row>
                        <Col lg={5} className="mb-4">
                            <img src={faqImg} alt="FAQ" className="img-fluid faq-side-img" />
                        </Col>

                        <Col lg={7}>
                            <Accordion defaultActiveKey="0" flush>
                                {faqs.map((faq, idx) => (
                                    <Accordion.Item eventKey={String(idx)} key={idx} className="mb-3">
                                        <Accordion.Header>{faq.question}</Accordion.Header>
                                        <Accordion.Body>{faq.answer}</Accordion.Body>
                                    </Accordion.Item>
                                ))}
                            </Accordion>
                        </Col>
                    </Row>
                </Container>
            </section>

        </div>
    );
};

export default Services;
